/**
 * TenantWebConfig — the runtime config of the customer web app (W-0, CAP-1).
 *
 * A subset of the future tenant config schema (plan 2.3). In W-0 it is
 * assembled in the browser from two parts:
 *   1. `/config.json`, rendered from container env at start-up (tenant id,
 *      region, API base URL, Firebase web config, Sentry, hosts), and
 *   2. the static brand/theme file `src/tenants/{tenantId}.json`.
 * Nothing tenant-specific is baked into the build. Phase 3 replaces this with
 * backend-served config.
 */
import { REGION_REGISTRY } from "./region_config.js";
/** Colour tokens a tenant theme may set (react-ui CSS variables, sans `--`). */
export const THEME_COLOR_TOKENS = [
    "background",
    "foreground",
    "card",
    "card-foreground",
    "popover",
    "popover-foreground",
    "primary",
    "primary-foreground",
    "secondary",
    "secondary-foreground",
    "muted",
    "muted-foreground",
    "accent",
    "accent-foreground",
    "destructive",
    "destructive-foreground",
    "border",
    "input",
    "ring",
    "cta",
    "cta-foreground",
    "status-success",
    "status-success-foreground",
    "status-warning",
    "status-warning-foreground",
    "status-error",
    "status-error-foreground",
    "status-info",
    "status-info-foreground",
    "sidebar",
    "sidebar-foreground",
    "sidebar-primary",
    "sidebar-primary-foreground",
    "sidebar-accent",
    "sidebar-accent-foreground",
    "sidebar-border",
    "sidebar-ring",
];
/** Tokens every tenant theme must supply (the load-bearing contrast pairs). */
export const REQUIRED_THEME_COLOR_TOKENS = [
    "background",
    "foreground",
    "primary",
    "primary-foreground",
    "muted-foreground",
    "input",
    "ring",
    "cta",
    "cta-foreground",
    "status-success",
    "status-success-foreground",
    "status-warning",
    "status-warning-foreground",
    "status-error",
    "status-error-foreground",
    "status-info",
    "status-info-foreground",
];
/** Feature gates a product tile can name (W-2). */
export const PRODUCT_TILE_FLAGS = ["item_categories"];
/** The five legacy (non-collector) item classifications (add item). */
export const LEGACY_ITEM_CLASSIFICATIONS = [
    "watch",
    "handbag",
    "jewellery",
    "camera",
    "laptop",
];
export const TENANT_REGIONS = {
    "kn-sg": "sg",
    "kn-hk": "hk",
};
/**
 * Tenant id for a web hostname: `sg.*` / `sg-*.*` → kn-sg, `hk.*` / `hk-*.*`
 * → kn-hk. Anything else (including the picker host) → null.
 */
export function tenantIdFromHostname(hostname) {
    const label = hostname.toLowerCase().split(".")[0] ?? "";
    if (/^sg(-|$)/.test(label))
        return "kn-sg";
    if (/^hk(-|$)/.test(label))
        return "kn-hk";
    return null;
}
function isObject(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isNonEmptyString(v) {
    return typeof v === "string" && v.trim().length > 0;
}
function isHttpUrl(v) {
    if (!isNonEmptyString(v))
        return false;
    try {
        const u = new URL(v);
        return u.protocol === "https:" || u.protocol === "http:";
    }
    catch {
        return false;
    }
}
/**
 * Merge `/config.json` with the tenant brand file and check the result.
 * Currency and timezone come from REGION_REGISTRY (never copied).
 */
export function buildTenantWebConfig(runtime, brandFile) {
    if (!isObject(runtime))
        return { ok: false, errors: ["config.json is not an object"] };
    if (!isObject(brandFile))
        return { ok: false, errors: ["tenant brand file is not an object"] };
    const region = runtime.region;
    const registry = REGION_REGISTRY[region];
    const merged = {
        ...runtime,
        locale: runtime.locale || brandFile.locale,
        currency: registry?.currency,
        timeZone: registry?.timezone,
        orgId: brandFile.orgId,
        brand: brandFile.brand,
        theme: brandFile.theme,
        productTiles: brandFile.productTiles,
        partnerDeclarations: brandFile.partnerDeclarations,
        i18nOverrides: brandFile.i18nOverrides,
        underwriters: brandFile.underwriters,
    };
    const errors = [];
    if (brandFile.tenantId !== runtime.tenantId) {
        errors.push(`brand file tenantId ${String(brandFile.tenantId)} ≠ config tenantId ${String(runtime.tenantId)}`);
    }
    if (brandFile.region !== runtime.region) {
        errors.push("brand file region does not match config region");
    }
    const result = validateTenantWebConfig(merged);
    if (!result.ok)
        errors.push(...result.errors);
    return errors.length ? { ok: false, errors } : result;
}
function validateProductTiles(tiles, region) {
    if (!Array.isArray(tiles))
        return ["productTiles must be a list"];
    const errors = [];
    const registry = REGION_REGISTRY[region];
    const offered = registry
        ? [...registry.availableItemClassifications, ...registry.collectorClassifications]
        : undefined;
    const seen = new Set();
    tiles.forEach((tile, i) => {
        if (!isObject(tile) || !isNonEmptyString(tile.category) || !isNonEmptyString(tile.underwriter)) {
            errors.push(`productTiles[${i}] needs a category and an underwriter`);
            return;
        }
        const category = tile.category;
        if (/travel/i.test(category)) {
            errors.push(`productTiles[${i}]: travel is never a web tile`);
            return;
        }
        if (offered && !offered.includes(category)) {
            errors.push(`productTiles[${i}]: ${category} is not offered in region ${String(region)}`);
        }
        if (seen.has(category))
            errors.push(`productTiles[${i}]: duplicate category ${category}`);
        seen.add(category);
        if (tile.flag !== undefined && !PRODUCT_TILE_FLAGS.includes(tile.flag)) {
            errors.push(`productTiles[${i}].flag must be one of ${PRODUCT_TILE_FLAGS.join(", ")}`);
        }
    });
    return errors;
}
function validatePartnerDeclarations(decls) {
    if (!Array.isArray(decls))
        return ["partnerDeclarations must be a list"];
    const errors = [];
    decls.forEach((d, i) => {
        if (!isObject(d)) {
            errors.push(`partnerDeclarations[${i}] must be an object`);
            return;
        }
        if (!isNonEmptyString(d.partnerId))
            errors.push(`partnerDeclarations[${i}].partnerId is required`);
        if (!Array.isArray(d.categories) ||
            d.categories.length === 0 ||
            !d.categories.every((c) => LEGACY_ITEM_CLASSIFICATIONS.includes(c))) {
            errors.push(`partnerDeclarations[${i}].categories must be a non-empty list of ${LEGACY_ITEM_CLASSIFICATIONS.join(", ")}`);
        }
        for (const k of ["labelKey", "windowNoticeKey"]) {
            const v = d[k];
            if (!isNonEmptyString(v) || !v.startsWith("legal.")) {
                errors.push(`partnerDeclarations[${i}].${k} must be a legal.* i18n key`);
            }
        }
    });
    return errors;
}
function validateCoverageLines(lines, path) {
    if (!Array.isArray(lines))
        return [`${path} must be a list`];
    const errors = [];
    lines.forEach((l, i) => {
        if (!isObject(l) || !isNonEmptyString(l.title)) {
            errors.push(`${path}[${i}].title is required`);
            return;
        }
        for (const k of ["detail", "note"]) {
            if (l[k] !== undefined && !isNonEmptyString(l[k]))
                errors.push(`${path}[${i}].${k} must be text`);
        }
        for (const k of ["only", "except"]) {
            const v = l[k];
            if (v !== undefined && (!Array.isArray(v) || !v.every(isNonEmptyString))) {
                errors.push(`${path}[${i}].${k} must be a list of classifications`);
            }
        }
    });
    return errors;
}
function validateUnderwriters(u) {
    if (!isObject(u))
        return ["underwriters must be an object"];
    const errors = [];
    for (const [uw, cfg] of Object.entries(u)) {
        const path = `underwriters.${uw}`;
        if (!isObject(cfg)) {
            errors.push(`${path} must be an object`);
            continue;
        }
        if (cfg.name !== undefined && !isNonEmptyString(cfg.name))
            errors.push(`${path}.name must be text`);
        if (cfg.coverage === undefined)
            continue;
        const cov = cfg.coverage;
        if (!isObject(cov)) {
            errors.push(`${path}.coverage must be an object`);
            continue;
        }
        errors.push(...validateCoverageLines(cov.covered, `${path}.coverage.covered`));
        errors.push(...validateCoverageLines(cov.notCovered, `${path}.coverage.notCovered`));
        if (!isHttpUrl(cov.wordingUrl))
            errors.push(`${path}.coverage.wordingUrl must be a URL`);
    }
    return errors;
}
export function validateTenantWebConfig(input) {
    const errors = [];
    if (!isObject(input))
        return { ok: false, errors: ["config is not an object"] };
    const c = input;
    if (!isNonEmptyString(c.tenantId))
        errors.push("tenantId is required");
    if (c.region !== "sg" && c.region !== "hk")
        errors.push("region must be sg or hk");
    if (isNonEmptyString(c.tenantId) &&
        TENANT_REGIONS[c.tenantId] &&
        TENANT_REGIONS[c.tenantId] !== c.region) {
        errors.push(`tenant ${c.tenantId} must use region ${TENANT_REGIONS[c.tenantId]}`);
    }
    if (!isNonEmptyString(c.orgId))
        errors.push("orgId is required");
    if (!isNonEmptyString(c.environment))
        errors.push("environment is required");
    if (!isHttpUrl(c.apiBaseUrl))
        errors.push("apiBaseUrl must be an http(s) URL");
    if (!Array.isArray(c.hosts) || c.hosts.length === 0 || !c.hosts.every(isNonEmptyString)) {
        errors.push("hosts must be a non-empty list of hostnames");
    }
    if (c.region === "sg" && !isHttpUrl(c.singpassServiceUrl)) {
        errors.push("singpassServiceUrl must be an http(s) URL in region sg");
    }
    else if (c.singpassServiceUrl !== undefined && !isHttpUrl(c.singpassServiceUrl)) {
        errors.push("singpassServiceUrl must be an http(s) URL");
    }
    if (!isNonEmptyString(c.locale))
        errors.push("locale is required");
    if (!isNonEmptyString(c.currency))
        errors.push("currency is required (from REGION_REGISTRY)");
    if (!isNonEmptyString(c.timeZone))
        errors.push("timeZone is required (from REGION_REGISTRY)");
    if (c.regionPicker !== undefined) {
        const p = c.regionPicker;
        if (!isObject(p) || !Array.isArray(p.hosts) || !isObject(p.regions)) {
            errors.push("regionPicker must have hosts[] and regions{}");
        }
        else {
            for (const [k, v] of Object.entries(p.regions)) {
                if (!isHttpUrl(v))
                    errors.push(`regionPicker.regions.${k} must be a URL`);
            }
        }
    }
    const fb = c.firebase;
    if (!isObject(fb)) {
        errors.push("firebase is required");
    }
    else {
        for (const k of ["apiKey", "authDomain", "projectId", "appId"]) {
            if (!isNonEmptyString(fb[k]))
                errors.push(`firebase.${k} is required`);
        }
        for (const k of ["storageBucket", "storageEmulatorHost"]) {
            if (fb[k] !== undefined && !isNonEmptyString(fb[k])) {
                errors.push(`firebase.${k} must be a non-empty string when set`);
            }
        }
    }
    const s = c.sentry;
    if (!isObject(s) || typeof s.dsn !== "string" || !isNonEmptyString(s.environment)) {
        errors.push("sentry.dsn (may be empty) and sentry.environment are required");
    }
    const b = c.brand;
    if (!isObject(b)) {
        errors.push("brand is required");
    }
    else {
        if (!isNonEmptyString(b.name))
            errors.push("brand.name is required");
        if (!isNonEmptyString(b.supportEmail))
            errors.push("brand.supportEmail is required");
        if (!isObject(b.legal) || !isHttpUrl(b.legal.termsUrl) || !isHttpUrl(b.legal.privacyUrl)) {
            errors.push("brand.legal.termsUrl and privacyUrl must be URLs");
        }
        else if (b.legal.faqUrl !== undefined && !isHttpUrl(b.legal.faqUrl)) {
            errors.push("brand.legal.faqUrl must be a URL");
        }
    }
    if (c.productTiles !== undefined) {
        errors.push(...validateProductTiles(c.productTiles, c.region));
    }
    if (c.partnerDeclarations !== undefined) {
        errors.push(...validatePartnerDeclarations(c.partnerDeclarations));
    }
    if (c.underwriters !== undefined) {
        errors.push(...validateUnderwriters(c.underwriters));
    }
    const t = c.theme;
    if (!isObject(t) || !isObject(t.colors)) {
        errors.push("theme.colors is required");
    }
    else {
        const colors = t.colors;
        for (const token of REQUIRED_THEME_COLOR_TOKENS) {
            if (!isNonEmptyString(colors[token]))
                errors.push(`theme.colors.${token} is required`);
        }
        for (const key of Object.keys(colors)) {
            if (!THEME_COLOR_TOKENS.includes(key)) {
                errors.push(`theme.colors.${key} is not a themable token`);
            }
        }
        if (!isObject(t.logo) || !isNonEmptyString(t.logo.src) || !isNonEmptyString(t.logo.alt)) {
            errors.push("theme.logo.src and theme.logo.alt are required");
        }
        if (t.ringOpacity !== undefined) {
            const o = t.ringOpacity;
            if (typeof o !== "number" || o < 0 || o > 1)
                errors.push("theme.ringOpacity must be 0–1");
        }
        if (t.fonts !== undefined) {
            const f = t.fonts;
            if (!isObject(f) || !isNonEmptyString(f.heading) || !isNonEmptyString(f.body)) {
                errors.push("theme.fonts.heading and theme.fonts.body are required");
            }
            else if (f.googleFontsUrl !== undefined &&
                !(isHttpUrl(f.googleFontsUrl) &&
                    new URL(f.googleFontsUrl).hostname === "fonts.googleapis.com")) {
                errors.push("theme.fonts.googleFontsUrl must be a fonts.googleapis.com URL");
            }
        }
    }
    if (c.i18nOverrides !== undefined && !isObject(c.i18nOverrides)) {
        errors.push("i18nOverrides must be an object");
    }
    return errors.length ? { ok: false, errors } : { ok: true, config: c };
}
